import { CommonModule } from '@angular/common'
import { Component, computed, inject, input, output } from '@angular/core'
import { _assert } from '@naturalcycles/js-lib'
import { CartDiscountType, Experiment, isThermometerKey, ProductKey } from '@naturalcycles/shared'
import { InfoPopupComponent } from '@src/app/components/modals/info-popup-modal/info-popup.component'
import { ModalService } from '@src/app/core/services/modal.service'
import { SignalStore } from '@src/app/core/store/signalStore'
import { ouraRingDiscount } from '@src/app/core/util/product.util'
import { SharedModule } from '@src/app/shared/shared.module'
import { EnrichedProduct } from '@src/app/shared/typings/interfaces/products'
import { ButtonComponentModule } from '../../app/components/button/button.component.module'

@Component({
  selector: 'app-measuring-device-card-revamped',
  imports: [ButtonComponentModule, SharedModule, CommonModule],
  templateUrl: './measuring-device-card-revamped.component.html',
  styleUrl: './measuring-device-card-revamped.component.scss',
})
export class MeasuringDeviceCardRevampedComponent {
  private store = inject(SignalStore)
  protected product = input.required<EnrichedProduct>()
  protected deviceSelected = output<ProductKey>()
  protected ProductKey = ProductKey

  protected productContent = computed((): MeasuringDeviceCardRevampedContent => {
    const content = measuringDeviceCardRevampedContentMap[this.product().key]
    _assert(
      content,
      `Content for product key ${this.product().key} is not defined in measuringDeviceCardRevampedContentMap`,
    )
    return content
  })

  protected ouraDiscountPercentage = ouraRingDiscount
  protected isNcHardware = computed(
    () => isThermometerKey(this.product().key) || this.product().key === ProductKey.B1,
  )

  protected showTag = computed(() => {
    const { key } = this.product()
    const cart = this.store.$cart()
    const discountCode = this.store.$discountCode()

    const isOuraRing = key === ProductKey.OURA_RING_DISCOUNT
    const isNcHardware = this.isNcHardware()
    const hasPredefinedCartApplied = discountCode?.type === CartDiscountType.PredefinedCart
    const thermItem = cart.items.find(item => isThermometerKey(item.key))
    const thermDiscounted = thermItem?.discountPercentage === 100
    const hideFreeTag = isNcHardware && hasPredefinedCartApplied && !thermDiscounted

    return isOuraRing || (isNcHardware && !hideFreeTag)
  })

  protected ab390Bucket = computed(
    () => this.store.$experiment().assignments[Experiment.WS_DISCOUNT_BADGES]?.bucket,
  )

  private modalService = inject(ModalService)

  async openInfoModal(): Promise<void> {
    void this.modalService.show({
      component: InfoPopupComponent,
      modalName: 'AppleDeviceCardInfoModal',
      controls: [],
    })
  }

  selected(productKey: ProductKey): void {
    this.deviceSelected.emit(productKey)
  }
}

const measuringDeviceCardRevampedContentMap: Partial<
  Record<ProductKey, MeasuringDeviceCardRevampedContent>
> = {
  [ProductKey.BT_THERMOMETER]: {
    description: 'Measure orally upon waking',
    bulletPoints: [
      'Backlit panel, silent mode',
      '<b>Free</b> with the Annual Plan',
      'Automatic sync via Bluetooth',
    ],
  },
  [ProductKey.OURA_RING_DISCOUNT]: {
    description: 'Wear it overnight',
    bulletPoints: [
      'Broader health metrics',
      'Our most popular integration',
      '<b>Exclusive offer</b> for NC° users',
    ],
  },
  [ProductKey.APPLE_WATCH]: {
    description: 'Wear it overnight',
    bulletPoints: [
      'Seamless measuring',
      'Automatic sync to NC°',
      'Fertility insights on your wrist',
    ],
  },
  [ProductKey.T3_THERMOMETER]: {
    description: 'Measure orally upon waking',
    bulletPoints: [
      'Automatic sync via Bluetooth',
      '<b>Free</b> with the Annual Plan',
      'Shipping included',
    ],
  },
  [ProductKey.THERMOMETER]: {
    description: 'Measure orally upon waking',
    bulletPoints: [
      'Saves temperature readings',
      '<b>Free</b> with the Annual Plan',
      'Shipping included',
    ],
  },
  // This experiment should be excluded from B1, but I put it here for defensiveness
  [ProductKey.B1]: {
    description: 'Measure overnight',
    bulletPoints: ['Comfortable wristband', 'Automatic sync to NC°', 'Free shipping'],
  },
}

interface MeasuringDeviceCardRevampedContent {
  description: string
  bulletPoints: string[]
}
